import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { BookMarked, Globe, Lock, Link2, Archive, Trash2, Eye, PenLine } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { mediaUrl, QuickShare } from "../components/story/StoryShared";

const TABS = [["all", "All"], ["draft", "Drafts"], ["published", "Published"], ["unlisted", "Unlisted"], ["private", "Private"], ["archived", "Archived"]];

/* My Travel Stories — home for every story created from Travel Diaries. */
export default function MyStories() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("all");
  const load = useCallback(() => api.get("/stories/mine").then((r) => setItems(r.data.items)).catch(() => setItems([])), []);
  useEffect(() => { load(); }, [load]);

  if (!items) return <div className="max-w-5xl mx-auto px-5 py-16 text-sm text-muted-foreground">Loading your stories…</div>;

  const match = (s) => {
    if (tab === "all") return s.status !== "archived";
    if (tab === "draft") return s.status === "draft";
    if (tab === "archived") return s.status === "archived";
    if (tab === "published") return s.status === "published";
    return s.status !== "archived" && s.visibility === tab;
  };
  const filtered = items.filter(match);

  const archive = async (id) => { await api.post(`/stories/${id}/archive`); load(); toast.success("Story archived"); };
  const del = async (id) => {
    if (!window.confirm("Delete this story? Your original diary and photos are not affected.")) return;
    await api.delete(`/stories/${id}`); load(); toast.success("Story deleted — diary untouched");
  };

  return (
    <div className="max-w-5xl mx-auto px-5 lg:px-8 py-8" data-testid="my-stories">
      <h1 className="font-heading font-900 text-3xl flex items-center gap-2 mb-1"><BookMarked className="w-7 h-7 text-fuchsia-600" /> My Travel Stories</h1>
      <p className="text-sm text-muted-foreground mb-5">Blogs, stories and PDFs created from your private Travel Diaries. Open any trip's diary → “Create Travel Story” to add more.</p>
      <div className="flex flex-wrap gap-1 border-b border-zinc-200 mb-5">
        {TABS.map(([t, label]) => (
          <button key={t} onClick={() => setTab(t)} data-testid={`stories-tab-${t}`}
            className={`px-3 py-2 text-sm font-600 border-b-2 -mb-px ${tab === t ? "border-fuchsia-600 text-zinc-900" : "border-transparent text-muted-foreground hover:text-zinc-900"}`}>{label}</button>
        ))}
      </div>
      {filtered.length === 0 ? (
        <div className="text-sm text-muted-foreground border border-dashed border-zinc-300 p-8 text-center space-y-3" data-testid="stories-empty">
          <p>Nothing here yet — open a trip's <b>Traveler Diary</b> and press <b>Create Travel Story</b>.</p>
          <Link to="/travel-hub#hub-trips" data-testid="stories-empty-trips-link"
            className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-700 text-white bg-fuchsia-600 hover:bg-fuchsia-700 transition-colors">
            <BookMarked className="w-4 h-4" /> Open My Trips &amp; Diaries
          </Link>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map((s) => (
            <div key={s.id} className="border border-zinc-300 bg-white flex flex-col" data-testid={`story-card-${s.id}`}>
              {s.cover_media_id
                ? <img src={mediaUrl(s.id, s.cover_media_id, true)} alt="" className="w-full h-36 object-cover" loading="lazy" />
                : <div className="w-full h-36 bg-gradient-to-br from-fuchsia-100 to-indigo-100 flex items-center justify-center text-fuchsia-300"><BookMarked className="w-10 h-10" /></div>}
              <div className="p-3 flex-1 flex flex-col">
                <div className="flex items-start justify-between gap-2">
                  <h2 className="font-heading font-900 text-base leading-snug">{s.title}</h2>
                  <span className={`text-[10px] font-700 uppercase px-1.5 py-0.5 rounded-full shrink-0 inline-flex items-center gap-1 ${s.status === "published" ? "bg-emerald-100 text-emerald-800" : s.status === "archived" ? "bg-zinc-200 text-zinc-600" : "bg-amber-100 text-amber-800"}`}>
                    {s.status === "published" ? (s.visibility === "public" ? <Globe className="w-2.5 h-2.5" /> : <Link2 className="w-2.5 h-2.5" />) : <Lock className="w-2.5 h-2.5" />}
                    {s.status === "published" ? s.visibility : s.status}
                  </span>
                </div>
                <div className="text-xs text-muted-foreground mt-1">{s.trip_title} · {s.style}{s.language ? ` · ${s.language}` : ""}</div>
                <div className="text-[11px] text-muted-foreground mt-0.5">Edited {new Date(s.updated_at).toLocaleDateString()}{s.status === "published" ? ` · ${s.views || 0} view${s.views === 1 ? "" : "s"}` : ""}</div>
                <div className="mt-auto pt-3 flex items-center gap-2">
                  <Link to={`/stories/${s.id}/edit`} data-testid={`story-edit-${s.id}`} className="inline-flex items-center gap-1 text-xs font-700 text-white bg-indigo-600 px-2.5 py-1.5"><PenLine className="w-3 h-3" /> Edit</Link>
                  {s.status === "published" && s.public_id && (
                    <>
                      <a href={`/story/${s.public_id}`} target="_blank" rel="noreferrer" data-testid={`story-view-${s.id}`} className="inline-flex items-center gap-1 text-xs font-700 border border-zinc-300 px-2.5 py-1.5"><Eye className="w-3 h-3" /> View</a>
                      <QuickShare publicId={s.public_id} title={s.title} storyId={s.id} />
                    </>
                  )}
                  {s.status !== "archived" && <button onClick={() => archive(s.id)} title="Archive" data-testid={`story-archive-${s.id}`} className="ml-auto text-muted-foreground hover:text-zinc-800"><Archive className="w-4 h-4" /></button>}
                  <button onClick={() => del(s.id)} title="Delete" data-testid={`story-delete-${s.id}`} className={`${s.status === "archived" ? "ml-auto" : ""} text-muted-foreground hover:text-destructive`}><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
